import React, { useCallback, useEffect, useRef, useState } from "react";
import { uploadFile, validateFile } from "../../utils/fileUpload";

const ADMIN_SESSION_KEY = "admin_password_verified";

// Square thumbnail of a partner's current logo, with an "Upload" / "Replace"
// button that lets admins set or update the logo directly from this page.
// Selected files are uploaded to Vercel Blob first (same path partners use
// in /partner-settings) and the resulting URL is then persisted to
// partner_profiles.logo_url via the set_logo admin action.
const LogoCell = ({ app, busy, onSetLogo }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState("");
  // Optimistic preview URL shown while the upload + DB write are in flight.
  const [optimisticUrl, setOptimisticUrl] = useState(null);

  const currentUrl = optimisticUrl || app.partner_profile_logo_url || null;
  const canEdit = Boolean(app.partner_user_id);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;

    const validation = validateFile(file, {
      allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif"],
      maxSize: 10 * 1024 * 1024,
    });
    if (!validation.isValid) {
      setLocalError(validation.error || "Invalid file.");
      return;
    }

    setLocalError("");
    setUploading(true);
    // Show the user's selection right away so the UI feels responsive while
    // we upload to Blob + persist to the DB.
    let previewUrl = null;
    try {
      previewUrl = URL.createObjectURL(file);
      setOptimisticUrl(previewUrl);
    } catch (_) {}

    try {
      const url = await uploadFile(file);
      // Hand off to parent: writes to DB, refreshes the list, and the new
      // URL replaces our optimistic preview on next render.
      await onSetLogo(url);
      setOptimisticUrl(url);
    } catch (err) {
      console.error("Logo upload failed:", err);
      setLocalError(err?.message || "Upload failed.");
      setOptimisticUrl(null);
    } finally {
      setUploading(false);
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch (_) {}
      }
    }
  };

  const isWorking = uploading || busy;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 88 }}>
      <div
        onClick={() => canEdit && !isWorking && inputRef.current?.click()}
        title={
          !canEdit
            ? "Partner has no user account yet — they must complete checkout before a logo can be set."
            : currentUrl
              ? "Click to replace logo"
              : "Click to upload a logo"
        }
        style={{
          width: 80,
          height: 80,
          borderRadius: 10,
          background: "#f5f5f5",
          border: "1px dashed #d1d5db",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: canEdit && !isWorking ? "pointer" : "not-allowed",
          opacity: canEdit ? 1 : 0.5,
          flexShrink: 0,
          position: "relative",
        }}
      >
        {currentUrl ? (
          <img
            src={currentUrl}
            alt="Partner logo"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <span style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", padding: 4, lineHeight: 1.2 }}>
            No logo
          </span>
        )}
        {isWorking && (
          <div style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            color: "#0f084b",
            fontWeight: 600,
          }}>
            Uploading…
          </div>
        )}
      </div>
      {canEdit && (
        <button
          type="button"
          disabled={isWorking}
          onClick={() => inputRef.current?.click()}
          style={{
            padding: "4px 8px",
            fontSize: 11,
            fontWeight: 600,
            color: "#0f084b",
            background: "transparent",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            cursor: isWorking ? "not-allowed" : "pointer",
            opacity: isWorking ? 0.6 : 1,
          }}
        >
          {currentUrl ? "Replace" : "Upload"}
        </button>
      )}
      {localError && (
        <div style={{ fontSize: 11, color: "#991b1b", maxWidth: 100, textAlign: "center", lineHeight: 1.3 }}>
          {localError}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
        style={{ display: "none" }}
        onChange={handleFile}
      />
    </div>
  );
};

const STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  converted: "Converted",
};

const STATUS_COLORS = {
  pending: { bg: "#fef3c7", fg: "#92400e" },
  approved: { bg: "#dcfce7", fg: "#166534" },
  rejected: { bg: "#fee2e2", fg: "#991b1b" },
  converted: { bg: "#dbeafe", fg: "#1e3a8a" },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return "—";
  }
};

// Short relative time like "just now" / "5m ago" / "yesterday" used in the
// "last sent" hint under the Send login setup button. Falls back to a short
// date for anything older than a week.
const formatRelative = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay === 1) return "yesterday";
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (_) {
    return "";
  }
};

// "Send login setup" / "Resend login setup" button. When the email has
// already been sent at least once, the button label flips to "Resend" and a
// small timestamp hint appears beneath it so admins can see at a glance when
// they last did this and avoid accidentally re-spamming a partner.
const LoginSetupButton = ({ app, busy, onClick }) => {
  const lastSent = app.last_login_setup_sent_at || null;
  const hasBeenSent = Boolean(lastSent);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 3 }}>
      <button
        disabled={busy}
        onClick={onClick}
        title={hasBeenSent ? `Last sent ${formatDate(lastSent)}. Click to resend.` : undefined}
        style={{
          padding: "6px 14px",
          border: "1px solid #7c3aed",
          borderRadius: 8,
          background: hasBeenSent ? "#f5f3ff" : "#fff",
          color: "#7c3aed",
          cursor: busy ? "not-allowed" : "pointer",
          fontSize: 13,
          fontWeight: 600,
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy
          ? "Sending…"
          : hasBeenSent
            ? "Resend login setup"
            : "Send login setup"}
      </button>
      {hasBeenSent && (
        <div
          title={formatDate(lastSent)}
          style={{
            fontSize: 11,
            color: "#6b7280",
            textAlign: "center",
            fontStyle: "italic",
            lineHeight: 1.2,
          }}
        >
          Last sent {formatRelative(lastSent)}
        </div>
      )}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const label = STATUS_LABELS[status] || status;
  const colors = STATUS_COLORS[status] || { bg: "#e5e7eb", fg: "#374151" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        background: colors.bg,
        color: colors.fg,
        fontSize: 12,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </span>
  );
};

const Field = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 6 }}>
      <span style={{ fontWeight: 600, color: "#0f084b", marginRight: 6 }}>{label}:</span>
      <span style={{ color: "#111827" }}>{value}</span>
    </div>
  );
};

// Inline editor for an application's details. Persists changes via the
// update_application admin action, which also mirrors the public-facing
// fields onto partner_profiles when the partner has been provisioned.
//
// Email is intentionally read-only because it's the join key linking
// partner_applications to users (changing it would orphan the user account).
const EditApplicationForm = ({ app, busy, onSave, onCancel }) => {
  const [form, setForm] = useState({
    businessName: app.business_name || "",
    contactName: app.contact_name || "",
    phone: app.phone || "",
    websiteUrl: app.website_url || "",
    instagram: app.instagram || "",
    locationCity: app.location_city || "",
    locationState: app.location_state || "",
    businessDescription: app.business_description || "",
    helpText: app.help_text || "",
    memberDiscountCode: app.member_discount_code || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    fontSize: 14,
    fontFamily: "inherit",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    boxSizing: "border-box",
    background: "#fff",
    color: "#111827",
  };
  const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#0f084b",
    marginBottom: 4,
  };
  const fieldStyle = { marginBottom: 12 };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || busy) return;
    if (!form.businessName.trim()) return;
    if (!form.contactName.trim()) return;
    setSubmitting(true);
    try {
      await onSave(form);
    } finally {
      setSubmitting(false);
    }
  };

  const isWorking = submitting || busy;

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        marginTop: 14,
        paddingTop: 14,
        borderTop: "1px solid #f3f4f6",
        fontSize: 14,
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Business name *</label>
          <input
            style={inputStyle}
            value={form.businessName}
            onChange={(e) => update("businessName", e.target.value)}
            required
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Contact name *</label>
          <input
            style={inputStyle}
            value={form.contactName}
            onChange={(e) => update("contactName", e.target.value)}
            required
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email (read-only)</label>
          <input
            style={{ ...inputStyle, background: "#f9fafb", color: "#6b7280" }}
            value={app.email || ""}
            readOnly
            title="Email is the lookup key tying this application to the partner's user account and can't be changed here."
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Phone</label>
          <input
            style={inputStyle}
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Website</label>
          <input
            style={inputStyle}
            value={form.websiteUrl}
            onChange={(e) => update("websiteUrl", e.target.value)}
            placeholder="https://example.com"
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Instagram</label>
          <input
            style={inputStyle}
            value={form.instagram}
            onChange={(e) => update("instagram", e.target.value)}
            placeholder="@handle or https://instagram.com/handle"
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>City</label>
          <input
            style={inputStyle}
            value={form.locationCity}
            onChange={(e) => update("locationCity", e.target.value)}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>State</label>
          <input
            style={inputStyle}
            value={form.locationState}
            onChange={(e) => update("locationState", e.target.value)}
          />
        </div>
        <div style={{ ...fieldStyle, gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Proposed member discount code</label>
          <input
            style={inputStyle}
            value={form.memberDiscountCode}
            onChange={(e) => update("memberDiscountCode", e.target.value)}
          />
        </div>
        <div style={{ ...fieldStyle, gridColumn: "1 / -1" }}>
          <label style={labelStyle}>About the business</label>
          <textarea
            style={{ ...inputStyle, minHeight: 90, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
            value={form.businessDescription}
            onChange={(e) => update("businessDescription", e.target.value)}
          />
        </div>
        <div style={{ ...fieldStyle, gridColumn: "1 / -1" }}>
          <label style={labelStyle}>How they can help hosts &amp; creators</label>
          <textarea
            style={{ ...inputStyle, minHeight: 90, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
            value={form.helpText}
            onChange={(e) => update("helpText", e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isWorking}
          style={{
            padding: "8px 16px",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            background: "#fff",
            color: "#374151",
            fontSize: 13,
            fontWeight: 600,
            cursor: isWorking ? "not-allowed" : "pointer",
            opacity: isWorking ? 0.6 : 1,
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isWorking}
          style={{
            padding: "8px 16px",
            border: 0,
            borderRadius: 8,
            background: "#0f084b",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: isWorking ? "not-allowed" : "pointer",
            opacity: isWorking ? 0.6 : 1,
          }}
        >
          {isWorking ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
};

// Show / Hide / Reset buttons that surface the manual visibility override.
const VisibilityToggleButtons = ({ app, busy, onShow, onHide, onReset }) => {
  const visible = Boolean(app.is_visible_on_partners_page);
  const override = app.visibility_override;
  const hasOverride = override === true || override === false;

  const baseBtn = {
    padding: "6px 14px",
    borderRadius: 8,
    cursor: busy ? "not-allowed" : "pointer",
    fontSize: 13,
    fontWeight: 600,
    opacity: busy ? 0.6 : 1,
  };

  return (
    <>
      {visible ? (
        <button
          disabled={busy}
          onClick={onHide}
          title="Force-hide this partner from /partners."
          style={{
            ...baseBtn,
            border: "1px solid #b45309",
            background: "#fff",
            color: "#b45309",
          }}
        >
          {busy ? "Updating…" : "Hide from partners page"}
        </button>
      ) : (
        <button
          disabled={busy}
          onClick={onShow}
          title="Force-show this partner on /partners (also adds a fallback partner subscription if missing)."
          style={{
            ...baseBtn,
            border: 0,
            background: "#0f084b",
            color: "#fff",
          }}
        >
          {busy ? "Updating…" : "Show on partners page"}
        </button>
      )}
      {hasOverride && (
        <button
          disabled={busy}
          onClick={onReset}
          title="Clear the manual override. Visibility will go back to being controlled by the partner's subscription state."
          style={{
            ...baseBtn,
            border: "1px solid #d1d5db",
            background: "#fff",
            color: "#374151",
          }}
        >
          Reset to automatic
        </button>
      )}
    </>
  );
};

// Small badge that tells the admin whether the partner is actually showing on
// the public /partners page right now, and *why*.
const VisibilityPill = ({ app }) => {
  if (!app.partner_profile_id) {
    return (
      <span
        title="No partner profile exists yet — applicant hasn't completed checkout."
        style={{
          display: "inline-block",
          padding: "3px 10px",
          borderRadius: 999,
          background: "#f3f4f6",
          color: "#6b7280",
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        No profile
      </span>
    );
  }
  const visible = Boolean(app.is_visible_on_partners_page);
  const override = app.visibility_override;
  let label;
  let tooltip;
  if (override === true) {
    label = "Live (manual)";
    tooltip = "Force-shown on /partners by admin override.";
  } else if (override === false) {
    label = "Hidden (manual)";
    tooltip = "Force-hidden from /partners by admin override.";
  } else if (visible) {
    label = "Live (auto)";
    tooltip = "Showing on /partners because the partner has an active subscription.";
  } else if (!app.has_logo) {
    label = "Not live · no logo";
    tooltip = "Partner has no logo uploaded yet. Won't render until they upload one in /partner-settings.";
  } else if (!app.has_partner_subscription) {
    label = "Not live · no sub";
    tooltip = "Partner has no active partner subscription. Click 'Show on partners page' to override.";
  } else {
    label = "Not live";
    tooltip = "Partner is not showing on /partners.";
  }
  const colors = visible
    ? { bg: "#ecfdf5", fg: "#065f46" }
    : { bg: "#fef2f2", fg: "#991b1b" };
  return (
    <span
      title={tooltip}
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        background: colors.bg,
        color: colors.fg,
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </span>
  );
};

const PartnerApplications = () => {
  const [authed, setAuthed] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [applications, setApplications] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [lastNotice, setLastNotice] = useState("");

  const [adminPassword, setAdminPassword] = useState(() => {
    try {
      return sessionStorage.getItem("admin_password_value") || "";
    } catch (_) {
      return "";
    }
  });

  useEffect(() => {
    try {
      const ok = sessionStorage.getItem(ADMIN_SESSION_KEY) === "true";
      if (ok && adminPassword) setAuthed(true);
    } catch (_) {}
  }, [adminPassword]);

  const fetchList = useCallback(
    async (statusFilter, overridePassword) => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch("/api/admin/partner-applications", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            adminPassword: overridePassword || adminPassword,
            action: "list",
            status: statusFilter && statusFilter !== "all" ? statusFilter : undefined,
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.ok) {
          throw new Error(data?.error || "load_failed");
        }
        setApplications(data.applications || []);
        const c = {};
        (data.counts || []).forEach((row) => {
          c[row.status] = row.count;
        });
        setCounts(c);
      } catch (err) {
        console.error("Failed to load applications:", err);
        setError("Couldn't load applications.");
      } finally {
        setLoading(false);
      }
    },
    [adminPassword]
  );

  useEffect(() => {
    if (authed) fetchList(filter);
  }, [authed, filter, fetchList]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      const res = await fetch("/api/admin/partner-applications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminPassword: passwordInput, action: "list", status: "pending" }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setAuthError("Incorrect password.");
        return;
      }
      try {
        sessionStorage.setItem(ADMIN_SESSION_KEY, "true");
        sessionStorage.setItem("admin_password_value", passwordInput);
      } catch (_) {}
      setAdminPassword(passwordInput);
      setAuthed(true);
      setPasswordInput("");
      fetchList(filter, passwordInput);
    } catch (_) {
      setAuthError("Couldn't verify password.");
    }
  };

  const mutate = async (id, action, extra = {}) => {
    if (!adminPassword) {
      setError("Session expired — refresh and sign in again.");
      return;
    }
    setBusyId(id);
    setLastNotice("");
    try {
      const endpoint = action === "send_login_setup"
        ? "/api/admin/send-login-setup"
        : "/api/admin/partner-applications";
      const payload = action === "send_login_setup"
        ? { adminPassword, applicationId: id }
        : { adminPassword, action, id, ...extra };
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data?.error || "action_failed");
      }
      if (action === "send_login_setup") {
        // Differentiate first send from a resend so admins know whether they
        // just spammed the partner or hit it for the first time.
        const wasResend = applications.some(
          (a) => a.id === id && a.last_login_setup_sent_at,
        );
        if (data.alreadyHadPassword) {
          setLastNotice(
            wasResend
              ? "Reminder noted. Partner already has a password — they can log in at /partner-login or use Forgot Password."
              : "Partner already has a password — they can log in at /partner-login or use Forgot Password."
          );
        } else {
          setLastNotice(
            wasResend
              ? `Login setup email re-sent to ${data.email || "partner"}.`
              : `Login setup email sent to ${data.email || "partner"}.`
          );
        }
      } else if (action === "approve") {
        if (data.emailSkipped) {
          setLastNotice(`Approved. Email wasn't sent (Resend not configured). Checkout URL: ${data.checkoutUrl}`);
        } else if (data.warning === "approval_saved_email_failed") {
          setLastNotice(`Approved, but approval email failed to send. Checkout URL: ${data.checkoutUrl}`);
        } else {
          setLastNotice(`Approved. Email sent. Checkout URL: ${data.checkoutUrl}`);
        }
      } else if (action === "reject") {
        setLastNotice("Application rejected.");
      } else if (action === "resend") {
        setLastNotice(`Approval email resent. Checkout URL: ${data.checkoutUrl}`);
      } else if (action === "set_logo") {
        setLastNotice(
          extra.logoUrl
            ? "Logo updated. The partner will reflect this on /partners on the next page load."
            : "Logo cleared."
        );
      } else if (action === "update_application") {
        setLastNotice(
          data.profileMirrored
            ? "Application updated. Public partner profile mirrored — changes are live on /partners on the next page load."
            : "Application updated. (No partner profile exists yet, so nothing to mirror to /partners.)"
        );
      } else if (action === "set_visibility") {
        if (extra.visible === true) {
          setLastNotice(
            data.warning
              ? `Partner force-shown on /partners. ⚠️ ${data.warning}`
              : "Partner is now visible on /partners."
          );
        } else if (extra.visible === false) {
          setLastNotice("Partner has been hidden from /partners.");
        } else {
          setLastNotice("Visibility override cleared. Partner now follows automatic (subscription-based) visibility.");
        }
      }
      await fetchList(filter);
    } catch (err) {
      console.error(`${action} failed:`, err);
      const errMsg = err?.message || "action_failed";
      const friendly = {
        no_partner_profile: "This applicant doesn't have a partner profile yet — they need to complete checkout first.",
        no_user_for_application: "No user account exists yet for this applicant.",
      }[errMsg];
      setError(friendly || `Couldn't ${action} that application.`);
    } finally {
      setBusyId(null);
    }
  };

  if (!authed) {
    return (
      <div
        style={{
          maxWidth: 420,
          margin: "80px auto",
          padding: 24,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          fontFamily: "-apple-system, Segoe UI, Roboto, Arial, sans-serif",
        }}
      >
        <h2 style={{ margin: "0 0 6px", color: "#0f084b" }}>Partner applications</h2>
        <p style={{ margin: "0 0 16px", color: "#6b7280", fontSize: 14 }}>
          Admin-only. Enter the admin password to continue.
        </p>
        <form onSubmit={handleAuth}>
          <input
            type="password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            placeholder="Admin password"
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              fontSize: 14,
              marginBottom: 10,
            }}
            autoFocus
          />
          {authError && (
            <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 10 }}>{authError}</div>
          )}
          <button
            type="submit"
            style={{
              width: "100%",
              padding: "10px 12px",
              background: "#0f084b",
              color: "#fff",
              border: 0,
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const filters = [
    { key: "pending", label: `Pending (${counts.pending || 0})` },
    { key: "approved", label: `Approved (${counts.approved || 0})` },
    { key: "converted", label: `Converted (${counts.converted || 0})` },
    { key: "rejected", label: `Rejected (${counts.rejected || 0})` },
    { key: "all", label: "All" },
  ];

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: "40px auto",
        padding: "0 24px 80px",
        fontFamily: "-apple-system, Segoe UI, Roboto, Arial, sans-serif",
      }}
    >
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ margin: "0 0 4px", color: "#0f084b" }}>Partner applications</h1>
        <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
          Review applications, approve to email a secure checkout link, or reject.
        </p>
      </header>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: "6px 14px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              border: filter === f.key ? "2px solid #0f084b" : "1px solid #d1d5db",
              background: filter === f.key ? "#0f084b" : "#fff",
              color: filter === f.key ? "#fff" : "#374151",
            }}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={() => fetchList(filter)}
          style={{
            marginLeft: "auto",
            padding: "6px 14px",
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            border: "1px solid #d1d5db",
            background: "#fff",
            color: "#374151",
          }}
        >
          Refresh
        </button>
      </div>

      {lastNotice && (
        <div
          style={{
            background: "#ecfdf5",
            color: "#065f46",
            border: "1px solid #a7f3d0",
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 13,
            wordBreak: "break-all",
          }}
        >
          {lastNotice}
        </div>
      )}

      {error && (
        <div
          style={{
            background: "#fef2f2",
            color: "#991b1b",
            border: "1px solid #fecaca",
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: "#6b7280" }}>Loading…</p>
      ) : applications.length === 0 ? (
        <p style={{ color: "#6b7280" }}>No applications in this bucket.</p>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {applications.map((app) => {
            const expanded = expandedId === app.id;
            return (
              <div
                key={app.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  background: "#fff",
                  padding: 18,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                  }}
                >
                  <LogoCell
                    app={app}
                    busy={busyId === app.id}
                    onSetLogo={(logoUrl) => mutate(app.id, "set_logo", { logoUrl })}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                      <h3 style={{ margin: 0, color: "#0f084b", fontSize: 18 }}>
                        {app.business_name || "(no business name)"}
                      </h3>
                      <StatusPill status={app.status} />
                      {(app.status === "approved" || app.status === "converted") && (
                        <VisibilityPill app={app} />
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>
                      {[app.contact_name, app.email].filter(Boolean).join(" · ")}
                      {app.location_city || app.location_state
                        ? ` · ${[app.location_city, app.location_state].filter(Boolean).join(", ")}`
                        : ""}
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                      Submitted {formatDate(app.created_at)}
                      {app.approved_at ? ` · Approved ${formatDate(app.approved_at)}` : ""}
                      {app.rejected_at ? ` · Rejected ${formatDate(app.rejected_at)}` : ""}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      onClick={() => {
                        if (expanded) {
                          setEditingId(null);
                          setExpandedId(null);
                        } else {
                          setExpandedId(app.id);
                        }
                      }}
                      style={{
                        padding: "6px 14px",
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        background: "#fff",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      {expanded ? "Hide details" : "Details"}
                    </button>
                    {app.status === "pending" && (
                      <>
                        <button
                          disabled={busyId === app.id}
                          onClick={() => mutate(app.id, "approve")}
                          style={{
                            padding: "6px 14px",
                            border: 0,
                            borderRadius: 8,
                            background: "#16a34a",
                            color: "#fff",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {busyId === app.id ? "Approving…" : "Approve & email"}
                        </button>
                        <button
                          disabled={busyId === app.id}
                          onClick={() => mutate(app.id, "reject")}
                          style={{
                            padding: "6px 14px",
                            border: "1px solid #dc2626",
                            borderRadius: 8,
                            background: "#fff",
                            color: "#dc2626",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {app.status === "approved" && (
                      <>
                        <button
                          disabled={busyId === app.id}
                          onClick={() => mutate(app.id, "resend")}
                          style={{
                            padding: "6px 14px",
                            border: "1px solid #0f084b",
                            borderRadius: 8,
                            background: "#fff",
                            color: "#0f084b",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          Resend email
                        </button>
                        <LoginSetupButton
                          app={app}
                          busy={busyId === app.id}
                          onClick={() => mutate(app.id, "send_login_setup")}
                        />
                        <button
                          disabled={busyId === app.id}
                          onClick={() => mutate(app.id, "reject")}
                          style={{
                            padding: "6px 14px",
                            border: "1px solid #dc2626",
                            borderRadius: 8,
                            background: "#fff",
                            color: "#dc2626",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          Revoke
                        </button>
                      </>
                    )}
                    {app.status === "converted" && (
                      <LoginSetupButton
                        app={app}
                        busy={busyId === app.id}
                        onClick={() => mutate(app.id, "send_login_setup")}
                      />
                    )}

                    {(app.status === "converted" || app.status === "approved") &&
                      app.partner_profile_id && (
                        <VisibilityToggleButtons
                          app={app}
                          busy={busyId === app.id}
                          onShow={() => mutate(app.id, "set_visibility", { visible: true })}
                          onHide={() => mutate(app.id, "set_visibility", { visible: false })}
                          onReset={() => mutate(app.id, "set_visibility", { visible: null })}
                        />
                      )}
                  </div>
                </div>

                {expanded && editingId === app.id && (
                  <EditApplicationForm
                    app={app}
                    busy={busyId === app.id}
                    onCancel={() => setEditingId(null)}
                    onSave={async (fields) => {
                      await mutate(app.id, "update_application", { fields });
                      setEditingId(null);
                    }}
                  />
                )}
                {expanded && editingId !== app.id && (
                  <div
                    style={{
                      marginTop: 14,
                      paddingTop: 14,
                      borderTop: "1px solid #f3f4f6",
                      fontSize: 14,
                      color: "#111827",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                      <button
                        type="button"
                        onClick={() => setEditingId(app.id)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #0f084b",
                          borderRadius: 8,
                          background: "#fff",
                          color: "#0f084b",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                        title="Edit business name, contact, location, socials, descriptions, and discount code. Email is not editable."
                      >
                        Edit details
                      </button>
                    </div>
                    <Field label="Phone" value={app.phone} />
                    <Field label="Website" value={app.website_url} />
                    <Field label="Instagram" value={app.instagram} />
                    <Field label="Proposed discount" value={app.member_discount_code} />
                    {app.business_description && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontWeight: 600, color: "#0f084b", marginBottom: 4 }}>
                          About the business
                        </div>
                        <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                          {app.business_description}
                        </div>
                      </div>
                    )}
                    {app.help_text && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontWeight: 600, color: "#0f084b", marginBottom: 4 }}>
                          How they can help hosts &amp; creators
                        </div>
                        <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{app.help_text}</div>
                      </div>
                    )}
                    {app.approval_token && (
                      <div style={{ marginTop: 12, fontSize: 12, color: "#6b7280", wordBreak: "break-all" }}>
                        <strong>Checkout URL:</strong> {window.location.origin}/partner-checkout?token=
                        {app.approval_token}
                      </div>
                    )}
                    {app.reviewer_note && (
                      <div style={{ marginTop: 10, fontSize: 13, color: "#6b7280" }}>
                        <strong>Reviewer note:</strong> {app.reviewer_note}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PartnerApplications;
